import type { TFunction } from "i18next";

/** 匠盟鼠标按键功能 type（与协议 / commonApi.MouseKeyFunction 一致） */
export const JIANGMENG_MOUSE_KEY_FUNCTION = {
  Disable: 0x00,
  MouseKey: 0x01,
  DPISwitch: 0x02,
  LeftRightRoll: 0x03,
  FireKey: 0x04,
  ShortcutKey: 0x05,
  Macro: 0x06,
  ReportRateSwitch: 0x07,
  LightSwitch: 0x08,
  ProfileSwitch: 0x09,
  DPILock: 0x0a,
  UpDownRoll: 0x0b,
} as const;

const MULTIMEDIA_TYPE = 1005;

function parseKeyType(type: unknown): number | null {
  if (type === undefined || type === null) return null;
  if (typeof type === "number") return type;
  const str = String(type);
  if (/^0x/i.test(str)) {
    const n = parseInt(str.replace(/^0x/i, ""), 16);
    return Number.isNaN(n) ? null : n;
  }
  const n = parseInt(str, 10);
  return Number.isNaN(n) ? null : n;
}

function normalizeParam(param: string | number | undefined): string {
  if (param === undefined || param === null || param === "") return "0x0000";
  if (typeof param === "string") return param.toLowerCase();
  return `0x${param.toString(16).padStart(4, "0")}`;
}

function tr(t: TFunction | undefined, key: string, fallback: string): string {
  if (!t) return fallback;
  const value = t(key, { defaultValue: "" });
  return value && value !== key ? value : fallback;
}

function isRawTypeParamLabel(label: string): boolean {
  return /^\d+\/0x[0-9a-f]+$/i.test(label.trim());
}

function parseRawTypeParamLabel(
  label: string
): { typeNum: number; paramKey: string } | null {
  const match = label.trim().match(/^(\d+)\/(0x[0-9a-f]+)$/i);
  if (!match) return null;
  const typeNum = parseInt(match[1], 10);
  if (Number.isNaN(typeNum)) return null;
  return { typeNum, paramKey: match[2].toLowerCase() };
}

/**
 * 匠盟改键 value → 展示文案（社区预览 / 分享序列化共用）
 */
export function resolveJiangmengMouseKeyLabel(
  keyValue: unknown,
  t?: TFunction
): string {
  if (!keyValue) {
    return tr(t, "mouse.changeKey.notSet", "未设置");
  }

  if (typeof keyValue === "string") {
    const trimmed = keyValue.trim();
    if (!trimmed) return tr(t, "mouse.changeKey.notSet", "未设置");
    const parsed = parseRawTypeParamLabel(trimmed);
    if (parsed) {
      return resolveJiangmengMouseKeyLabel(
        [String(parsed.typeNum), parsed.paramKey],
        t
      );
    }
    return trimmed;
  }

  if (!Array.isArray(keyValue) || keyValue.length === 0) {
    return tr(t, "mouse.changeKey.notSet", "未设置");
  }

  const typeNum = parseKeyType(keyValue[0]);
  if (typeNum === null) {
    return tr(t, "mouse.changeKey.notSet", "未设置");
  }

  const paramKey = normalizeParam(keyValue[1] as string | number);

  if (typeNum === JIANGMENG_MOUSE_KEY_FUNCTION.MouseKey) {
    const mouseKeyMap: Record<string, string> = {
      "0x0001": tr(t, "mouse.changeKey.leftButton", "左键"),
      "0x0002": tr(t, "mouse.changeKey.rightButton", "右键"),
      "0x0004": tr(t, "mouse.changeKey.middleButton", "中键"),
      "0x0008": tr(t, "mouse.changeKey.backward", "后退"),
      "0x0010": tr(t, "mouse.changeKey.forward", "前进"),
      "0x0100": tr(t, "mouse.changeKey.leftButton", "左键"),
      "0x0200": tr(t, "mouse.changeKey.rightButton", "右键"),
      "0x0400": tr(t, "mouse.changeKey.middleButton", "中键"),
      "0x0800": tr(t, "mouse.changeKey.backward", "后退"),
      "0x1000": tr(t, "mouse.changeKey.forward", "前进"),
    };
    return mouseKeyMap[paramKey] ?? paramKey;
  }

  if (typeNum === MULTIMEDIA_TYPE) {
    const multimediaMap: Record<string, string> = {
      "0x006f": tr(t, "mouse.changeKey.brightnessUp", "亮度+"),
      "0x0070": tr(t, "mouse.changeKey.brightnessDown", "亮度-"),
      "0x00e9": tr(t, "mouse.changeKey.volumeUp", "音量+"),
      "0x00ea": tr(t, "mouse.changeKey.volumeDown", "音量-"),
      "0x00e2": tr(t, "mouse.changeKey.mute", "静音"),
      "0x00cd": tr(t, "mouse.changeKey.playPause", "播放/暂停"),
      "0x00b5": tr(t, "mouse.changeKey.nextTrack", "下一曲"),
      "0x00b6": tr(t, "mouse.changeKey.previousTrack", "上一曲"),
      "0x00b7": tr(t, "mouse.changeKey.stopPlayback", "停止"),
      "0x0183": tr(t, "mouse.changeKey.mediaPlayer", "播放器"),
      "0x018a": tr(t, "mouse.changeKey.email", "邮件"),
      "0x0192": tr(t, "mouse.changeKey.calculator", "计算器"),
      "0x0194": tr(t, "mouse.changeKey.myComputer", "我的电脑"),
      "0x0223": tr(t, "mouse.changeKey.homePage", "主页"),
      "0x0221": tr(t, "mouse.changeKey.search", "搜索"),
      "0x0225": tr(t, "mouse.changeKey.webForward", "网页前进"),
      "0x0224": tr(t, "mouse.changeKey.webBack", "网页后退"),
      "0x0226": tr(t, "mouse.changeKey.webStop", "网页停止"),
      "0x0227": tr(t, "mouse.changeKey.refresh", "刷新"),
    };
    return multimediaMap[paramKey] ?? paramKey;
  }

  if (typeNum === JIANGMENG_MOUSE_KEY_FUNCTION.DPISwitch) {
    const dpiMap: Record<string, string> = {
      "0x0100": tr(t, "mouse.changeKey.dpiCycle", "DPI循环"),
      "0x0200": tr(t, "mouse.changeKey.dpiIncrease", "DPI+"),
      "0x0300": tr(t, "mouse.changeKey.dpiDecrease", "DPI-"),
    };
    return dpiMap[paramKey] ?? tr(t, "mouse.changeKey.dpiSwitch", "DPI切换");
  }

  if (typeNum === JIANGMENG_MOUSE_KEY_FUNCTION.LeftRightRoll) {
    return paramKey === "0x0100"
      ? tr(t, "mouse.changeKey.tiltLeft", "左滚")
      : tr(t, "mouse.changeKey.tiltRight", "右滚");
  }

  if (typeNum === JIANGMENG_MOUSE_KEY_FUNCTION.UpDownRoll) {
    return paramKey === "0x0100"
      ? tr(t, "mouse.changeKey.scrollUp", "上滚")
      : tr(t, "mouse.changeKey.scrollDown", "下滚");
  }

  if (typeNum === JIANGMENG_MOUSE_KEY_FUNCTION.FireKey) {
    const interval = keyValue[1] ?? 10;
    const count = keyValue[2] !== undefined ? keyValue[2] : 1;
    const countDisplay =
      count === 0 || count === "0"
        ? tr(t, "mouse.changeKey.continuous", "连续")
        : String(count);
    return `${tr(t, "mouse.changeKey.fireKey", "火力键")}(${tr(t, "mouse.changeKey.interval", "间隔")}:${interval}ms, ${tr(t, "mouse.changeKey.count", "次数")}:${countDisplay})`;
  }

  if (typeNum === JIANGMENG_MOUSE_KEY_FUNCTION.Macro) {
    const slot = parseInt(paramKey.replace(/^0x/i, ""), 16);
    const index = Number.isFinite(slot) && slot >= 0 ? slot & 0xff || slot : 0;
    return `M${index}`;
  }

  if (typeNum === JIANGMENG_MOUSE_KEY_FUNCTION.Disable) {
    return tr(t, "mouse.changeKey.disabled", "禁用");
  }

  if (typeNum === JIANGMENG_MOUSE_KEY_FUNCTION.ReportRateSwitch) {
    return tr(t, "mouse.changeKey.reportRateSwitch", "回报率切换");
  }

  if (typeNum === JIANGMENG_MOUSE_KEY_FUNCTION.LightSwitch) {
    return tr(t, "mouse.changeKey.lightSwitch", "灯光切换");
  }

  if (typeNum === JIANGMENG_MOUSE_KEY_FUNCTION.DPILock) {
    return tr(t, "mouse.changeKey.dpiLock", "DPI锁定");
  }

  if (typeNum === JIANGMENG_MOUSE_KEY_FUNCTION.ProfileSwitch) {
    const profileMap: Record<string, string> = {
      "0x0100": tr(t, "mouse.changeKey.profile1", "板载1"),
      "0x0200": tr(t, "mouse.changeKey.profile2", "板载2"),
      "0x0300": tr(t, "mouse.changeKey.profile3", "板载3"),
      "0x0400": tr(t, "mouse.changeKey.profile4", "板载4"),
      "0x0500": tr(t, "mouse.changeKey.profileCycle", "板载循环"),
    };
    return (
      profileMap[paramKey] ?? tr(t, "mouse.changeKey.profileSwitch", "板载切换")
    );
  }

  if (typeNum === JIANGMENG_MOUSE_KEY_FUNCTION.ShortcutKey) {
    const keys = keyValue.slice(1).filter(Boolean).map(String);
    if (keys.length > 0) return keys.join(" + ");
    return tr(t, "mouse.changeKey.notSet", "未设置");
  }

  return `${typeNum}/${paramKey}`;
}

export function normalizeMouseChangeKeyDisplayLabel(
  label: string | undefined,
  rawValue: unknown,
  t?: TFunction
): string | undefined {
  if (rawValue != null) {
    const resolved = resolveJiangmengMouseKeyLabel(rawValue, t);
    if (resolved && !isRawTypeParamLabel(resolved)) {
      return resolved;
    }
  }
  if (!label) return undefined;
  if (isRawTypeParamLabel(label)) {
    const resolved = resolveJiangmengMouseKeyLabel(label, t);
    return isRawTypeParamLabel(resolved) ? label : resolved;
  }
  return label;
}
